import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { MapPin, Loader } from 'lucide-react';

const AddressAutocomplete = ({ onPlaceSelect, initialValue = '', placeholder = 'Enter address', searchType = 'address' }) => {
  const { isDarkMode } = useDarkMode();
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const debounceTimeout = useRef(null);
  const dropdownRef = useRef(null);

  const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Different search types
  const getIncludedTypes = () => {
    if (searchType === 'mosque') {
      // Don't restrict types - let Google find mosques with their full addresses
      return undefined;
    }
    return ['street_address', 'premise', 'subpremise'];
  };

  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  // Generate session token for billing optimization
  useEffect(() => {
    setSessionToken(Math.random().toString(36).substring(7));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (input) => {
    if (!input || input.length < 3) {
      setSuggestions([]);
      return;
    }

    if (!API_KEY) {
      setError('Google Maps API key is not configured');
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': API_KEY,
        },
        body: JSON.stringify({
          input,
          sessionToken,
          ...(getIncludedTypes() ? { includedPrimaryTypes: getIncludedTypes() } : {}),
          languageCode: 'en'
        })
      });

      const data = await response.json();

      if (data.suggestions) {
        setSuggestions(data.suggestions);
        setShowDropdown(true);
        setError('');
      } else if (data.error) {
        console.error('[AddressAutocomplete] API Error:', data.error);
        setError(`API Error: ${data.error.message || data.error.status}`);
        setSuggestions([]);
        setShowDropdown(false);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (err) {
      console.error('[AddressAutocomplete] Error fetching suggestions:', err);
      setError('Failed to fetch address suggestions: ' + err.message);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [API_KEY, sessionToken]);

  const fetchPlaceDetails = async (placeId) => {
    if (!API_KEY) {
      setError('Google Maps API key is not configured');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`https://places.googleapis.com/v1/places/${placeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': API_KEY,
          'X-Goog-FieldMask': 'addressComponents,formattedAddress,location,displayName'
        }
      });

      const place = await response.json();

      // Parse address components
      const addressData = {
        street: '',
        postcode: '',
        city: '',
        country: '',
        countryCode: '',
        latitude: null,
        longitude: null,
        formattedAddress: place.formattedAddress || '',
        placeName: place.displayName?.text || '' // For mosque names
      };

      let streetNumber = '';
      let route = '';

      if (place.addressComponents) {
        place.addressComponents.forEach(component => {
          const types = component.types || [];

          if (types.includes('street_number')) {
            streetNumber = component.longText;
          }
          if (types.includes('route')) {
            route = component.longText;
          }
          if (types.includes('postal_code')) {
            addressData.postcode = component.longText;
          }
          if (types.includes('locality')) {
            addressData.city = component.longText;
          }
          if (types.includes('administrative_area_level_2') && !addressData.city) {
            addressData.city = component.longText;
          }
          if (types.includes('country')) {
            addressData.country = component.longText;
            addressData.countryCode = component.shortText;
          }
        });
      }

      // Combine street number and route
      if (streetNumber && route) {
        addressData.street = `${route} ${streetNumber}`;
      } else if (route) {
        addressData.street = route;
      } else if (streetNumber) {
        addressData.street = streetNumber;
      } else if (place.formattedAddress) {
        // Fallback: extract street from formatted address (first line before comma)
        const parts = place.formattedAddress.split(',');
        if (parts.length > 0) {
          const firstPart = parts[0].trim();
          // Only use if it's not the place name
          if (firstPart !== place.displayName?.text) {
            addressData.street = firstPart;
          }
        }
      }

      // Get coordinates
      if (place.location) {
        addressData.latitude = place.location.latitude;
        addressData.longitude = place.location.longitude;
      }

      // Update input value
      setInputValue(addressData.formattedAddress);
      setShowDropdown(false);

      // New session token for next autocomplete
      setSessionToken(Math.random().toString(36).substring(7));

      // Call parent callback
      if (onPlaceSelect) {
        onPlaceSelect(addressData);
      }
    } catch (err) {
      console.error('Error fetching place details:', err);
      setError('Failed to fetch place details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Debounce API calls
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    fetchPlaceDetails(suggestion.placePrediction.placeId);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10">
        {loading ? (
          <Loader className={`w-5 h-5 animate-spin ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`} />
        ) : (
          <MapPin className={`w-5 h-5 ${
            isDarkMode ? 'text-gray-500' : 'text-gray-400'
          }`} />
        )}
      </div>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`w-full pl-12 pr-4 py-2.5 rounded-xl border transition-colors ${
          isDarkMode
            ? 'bg-[#0A0A0A] border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-emerald-500'
        } focus:outline-none`}
      />

      {/* Dropdown with suggestions */}
      {showDropdown && suggestions.length > 0 && (
        <div className={`absolute z-20 w-full mt-2 rounded-xl border shadow-lg overflow-hidden ${
          isDarkMode
            ? 'bg-[#111111] border-gray-700'
            : 'bg-white border-gray-200'
        }`}>
          <ul className="max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`px-4 py-3 cursor-pointer transition-colors border-b last:border-b-0 ${
                  isDarkMode
                    ? 'border-gray-800 hover:bg-[#1A1A1A]'
                    : 'border-gray-100 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <MapPin className={`w-4 h-4 mt-1 flex-shrink-0 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {suggestion.placePrediction.structuredFormat?.mainText?.text ||
                       suggestion.placePrediction.text?.text}
                    </p>
                    <p className={`text-xs truncate ${
                      isDarkMode ? 'text-gray-500' : 'text-gray-600'
                    }`}>
                      {suggestion.placePrediction.structuredFormat?.secondaryText?.text}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className={`mt-2 text-xs ${
          isDarkMode ? 'text-red-400' : 'text-red-600'
        }`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
